// Licencias legales (Arts. 152-155 CT): maternidad, paternidad, adopción,
// lactancia, etc. Nómina → Movimientos → Licencias. Protegen las faltas de
// asistencia, ajustan el rol (subsidio IESS / sin sueldo) y la jornada de
// lactancia; validación de topes legales en la BD.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import {
  Campo, Modal, Switch, fecha, fieldCls, nomRpc, TIPOS_LICENCIA, useDialogoEnLinea,
  type EmpleadoRow, type LicenciaRow,
} from './shared';

const VACIA = { id: '', empleado_id: '', tipo: 'maternidad', desde: '', hasta: '', pct_empleador: '25', certificado: '', observacion: '' };

export function Licencias() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [soloVigentes, setSoloVigentes] = useState(true);
  const [q, setQ] = useState('');
  const { data: rows = [], isFetching } = useQuery<LicenciaRow[]>({
    queryKey: ['nom-licencias', soloVigentes],
    queryFn: () => nomRpc<LicenciaRow[]>('nom_licencias', { _empleado_id: null, _solo_vigentes: soloVigentes }),
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });
  const filas = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? rows.filter((r) => r.razon_social.toLowerCase().includes(t) || r.identificacion.includes(t)) : rows;
  }, [rows, q]);

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof VACIA>(VACIA);
  const info = TIPOS_LICENCIA[form.tipo];
  const dias = form.desde && form.hasta && form.hasta >= form.desde
    ? Math.round((new Date(form.hasta).getTime() - new Date(form.desde).getTime()) / 86400000) + 1 : 0;

  const abrir = (l?: LicenciaRow) => {
    if (l) setForm({ id: l.id, empleado_id: l.empleado_id, tipo: l.tipo, desde: l.desde.slice(0, 10), hasta: l.hasta.slice(0, 10), pct_empleador: String(l.pct_empleador), certificado: l.certificado ?? '', observacion: l.observacion ?? '' });
    else setForm(VACIA);
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_licencia_guardar', {
      _data: {
        id: form.id || null, empleado_id: form.empleado_id, tipo: form.tipo,
        desde: form.desde, hasta: form.hasta,
        pct_empleador: form.tipo === 'maternidad' ? Number(form.pct_empleador) : null,
        certificado: form.certificado || null, observacion: form.observacion || null,
      },
    }),
    onSuccess: () => { toast.success('Licencia guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-licencias'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_licencia_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Licencia eliminada'); void qc.invalidateQueries({ queryKey: ['nom-licencias'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const headersExp = ['N°', 'Empleado', 'Tipo', 'Desde', 'Hasta', 'Días', '% empleador', 'Respaldo', 'Estado'];
  const filasExport: Celda[][] = [
    ...filas.map((l, i): Celda[] => [
      i + 1, l.razon_social, TIPOS_LICENCIA[l.tipo]?.label ?? l.tipo, fecha(l.desde), fecha(l.hasta),
      Number(l.dias), Number(l.pct_empleador), l.certificado ?? '—',
      l.vigente ? 'Vigente' : (l.desde > new Date().toISOString() ? 'Programada' : 'Concluida'),
    ]),
    ['', '', '', '', 'TOTAL', filas.reduce((a, l) => a + Number(l.dias), 0), '', '', ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Licencias</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones nombre={`licencias-${fechaLocal()}`} titulo="Licencias"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 5, 6]} disabled={filas.length === 0} />
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Registrar licencia</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Las licencias protegen automáticamente al empleado en Asistencia (no generan faltas), ajustan el rol
        (subsidio IESS de maternidad, días sin sueldo) y la jornada de lactancia (6 h). El despido durante
        maternidad/lactancia es INEFICAZ (Arts. 153/195.1) — Liquidaciones lo advierte.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <input className={`${fieldCls} w-72`} placeholder="Filtrar por nombre o cédula…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Switch checked={soloVigentes} onChange={setSoloVigentes} label="Solo vigentes/futuras" />
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Licencias ({filas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Desde</th>
              <th className="py-2 pr-3">Hasta</th><th className="py-2 pr-3 text-right">Días</th>
              <th className="py-2 pr-3 text-right">% empleador</th><th className="py-2 pr-3">Respaldo</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filas.length === 0 && !isFetching && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin licencias</td></tr>}
              {filas.map((l) => (
                <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{l.razon_social}<div className="text-xs text-muted-foreground font-mono">{l.identificacion}</div></td>
                  <td className="py-2 pr-3">{TIPOS_LICENCIA[l.tipo]?.label ?? l.tipo}</td>
                  <td className="py-2 pr-3 font-mono">{fecha(l.desde)}</td>
                  <td className="py-2 pr-3 font-mono">{fecha(l.hasta)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{l.dias}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{Number(l.pct_empleador)}%</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{l.certificado ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${l.vigente ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>
                      {l.vigente ? 'Vigente' : (l.desde > new Date().toISOString() ? 'Programada' : 'Concluida')}
                    </span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(l)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar('¿Eliminar la licencia?', { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(l.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={form.id ? 'Editar licencia' : 'Registrar licencia'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empleado *">
              <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })} disabled={!!form.id}>
                <option value="">— Selecciona —</option>
                {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
              </select>
            </Campo>
            <Campo label="Tipo *">
              <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {Object.entries(TIPOS_LICENCIA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Campo>
            {info && <div className="text-xs text-muted-foreground">{info.nota} Máximo legal: {info.tope} días.</div>}
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Desde *"><input type="date" className={fieldCls} value={form.desde} onChange={(e) => setForm({ ...form, desde: e.target.value })} /></Campo>
              <Campo label="Hasta *"><input type="date" className={fieldCls} value={form.hasta} onChange={(e) => setForm({ ...form, hasta: e.target.value })} /></Campo>
              {form.tipo === 'maternidad' && (
                <Campo label="% a cargo del empleador"><input type="number" min="0" max="100" className={fieldCls} value={form.pct_empleador} onChange={(e) => setForm({ ...form, pct_empleador: e.target.value })} /></Campo>
              )}
            </div>
            {dias > 0 && (
              <div className={`text-sm ${info && dias > info.tope ? 'text-urgente' : ''}`}>
                {dias} días{info && dias > info.tope ? ` — excede el máximo legal de ${info.tope}` : ''}
              </div>
            )}
            <Campo label="Documento de respaldo"><input className={fieldCls} value={form.certificado} onChange={(e) => setForm({ ...form, certificado: e.target.value })} placeholder="Certificado médico IESS, acta…" /></Campo>
            <Campo label="Observación"><input className={fieldCls} value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.empleado_id || !form.desde || !form.hasta} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
